import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    CircularProgress,
    Alert,
    Container,
    Tabs,
    Tab,
    Pagination,
    IconButton
} from '@mui/material';
import {    
    Refresh as RefreshIcon
} from '@mui/icons-material';
import TypeTag from '../shared/TypeTag';
import { useNavigate } from 'react-router-dom';
import { learnerService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateFormatter';
import { tokens } from '../../theme';
const AllRecommendations = () => {
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentTab, setCurrentTab] = useState(0);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 12,
        total: 0,
        totalPages: 0
    });
    
    const navigate = useNavigate();
    const { showToast } = useToast();

    const tabTypes = ['all', 'course', 'learning path'];
    const tabLabels = ['All', 'Courses', 'Learning Paths'];

    useEffect(() => {
        fetchRecommendations();
    }, [currentTab, pagination.page]);

    const fetchRecommendations = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await learnerService.getAllSkillBasedRecommendations(
                pagination.page,
                pagination.limit,
                tabTypes[currentTab]
            );
            
            if (response.data.success) {
                setRecommendations(response.data.data);
                setPagination(prev => ({
                    ...prev,
                    ...response.data.pagination
                }));
            } else {
                setError(response.data.message || 'Failed to load recommendations');
            }
        } catch (error) {
            console.error('Error fetching recommendations:', error);
            setError('Failed to load recommendations');
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handlePageChange = (event, page) => {
        setPagination(prev => ({ ...prev, page }));
    };

    const handleItemClick = (item) => {
        const id = item.id ?? item.courseId;
        console.log('Navigating to item:', item);
        if (!id) return;
        if (item.type === 'course') {
            navigate(`/course/preview/${item.id}`);
        } else if (item.type === 'learning path') {
            navigate(`/learning_path/preview/${item.id}`);
        }
    };    

    const getDefaultImage = (type) => {
        return type === 'course' 
            ? '/images/nopreview.png'
            : '/images/nopreview.png';
    };

    const getImagePath = (item) =>{
        if (!item.cover_picture) {
            return getDefaultImage(item.type);
        }
        
        if (item.type === 'course') {
            return `/uploads/courses/${item.cover_picture}`;
        } else if (item.type === 'learning path') {
            return `/uploads/learning_path/${item.cover_picture}`;
        }
        
        return getDefaultImage(item.type);
    }
    return (
        <Container maxWidth="xl">
            <Box sx={{ py: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4" component="h1">
                        Recommended for You
                    </Typography>
                    <IconButton onClick={fetchRecommendations}>
                        <RefreshIcon />
                    </IconButton>
                </Box>

                <Card>
                    <CardContent>
                        <Tabs 
                            value={currentTab} 
                            onChange={handleTabChange}
                            sx={{ mb: 3 }}
                        >
                            {tabLabels.map((label, index) => (
                                <Tab key={index} label={label} />
                            ))}
                        </Tabs>

                        {loading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                                <CircularProgress />
                                <Typography variant="body2" sx={{ ml: 2 }}>
                                    Loading recommendations...
                                </Typography>
                            </Box>
                        ) : error ? (
                            <Alert 
                                severity="info" 
                                action={
                                    error.includes('skills') ? (
                                        <Button 
                                            color="inherit" 
                                            size="small"
                                            onClick={() => navigate('/users/profile')}
                                        >
                                            Add Skills
                                        </Button>
                                    ) : null
                                }
                            >
                                {error}
                            </Alert>
                        ) : recommendations.length === 0 ? (
                            <Box textAlign="center" py={8}>
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    No recommendations found
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    Add your interested skills to get personalized recommendations
                                </Typography>
                                <Button 
                                    variant="contained" 
                                    onClick={() => navigate('/users/profile')}
                                >
                                    Add Skills
                                </Button>
                            </Box>
                        ) : (
                            <>
                                <Grid container spacing={3}>
                                    {recommendations.map((item) => (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={`${item.type}-${item.id}`}>
                                            <Card
                                                sx={{ 
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: 3
                                                    }
                                                }}
                                                onClick={() => handleItemClick(item)}
                                            >
                                                <Box
                                                    sx={{
                                                        height: 120,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: tokens.color.bgTertiary,
                                                    }}
                                                    >
                                                    <TypeTag type={item.type} />
                                                </Box>
                                                {/* <CardContent sx={{ flexGrow: 1 }}> */}
                                                <CardContent
                                                    sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'space-between',
                                                    flexGrow: 1,
                                                    }}
                                                >
                                                    <Box display="flex" alignItems="center" mb={1}>
                                                        <TypeTag type={item.type} />
                                                    </Box>
                                                    
                                                    <Typography 
                                                        variant="h6" 
                                                        component="h3"
                                                        sx={{ 
                                                            fontWeight: 600,
                                                            mb: 1,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            lineHeight: 1.3
                                                        }}
                                                    >
                                                        {item.name}
                                                    </Typography>
                                                    
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto">
                                                        <Typography 
                                                            variant="body2" 
                                                            color="primary.main"
                                                            sx={{ fontWeight: 500 }}
                                                        >
                                                            {item.skillMatchCount} skill{item.skillMatchCount !== 1 ? 's' : ''} match
                                                        </Typography>
                                                        <Typography 
                                                            variant="caption" 
                                                            color="text.secondary"
                                                        >
                                                            {formatDate(item.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>

                                {pagination.totalPages > 1 && (
                                    <Box display="flex" justifyContent="center" mt={4}>
                                        <Pagination
                                            count={pagination.totalPages}
                                            page={pagination.page}
                                            onChange={handlePageChange}
                                            color="primary"
                                            size="large"
                                        />
                                    </Box>
                                )}

                                <Box mt={3} textAlign="center">
                                    <Typography variant="body2" color="text.secondary">
                                        Showing {recommendations.length} of {pagination.total} recommendations
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Container>
    );
};

export default AllRecommendations;